<template lang="pug">
v-dialog(v-model="dialogVisible" max-width="400" persistent)
  v-card.round-xl.neutral200.pa-4(flat)
    div.d-flex
      div
        div.text-md.font-medium.neutral700--text Adjustment calculator
        div.text-xs.font-normal.neutral700--text Calculate the adjustment needed to reach your desired total amount.
      v-spacer
      v-btn(icon x-small @click="onClose")
        v-icon(size="20" color="neutral700") mdi-close

    //- Taxable subtotal
    div.d-flex.align-center.mt-3
      div.text-xs.font-medium.neutral700--text Taxable subtotal
      v-spacer
      div.text-sm.font-medium.neutral700--text {{ taxableSubtotal | displayPrice($currency) }}

    //- Non-taxable subtotal (only show if > 0)
    div.d-flex.align-center.mt-2(v-if="nonTaxableSubtotal > 0")
      div.text-xs.font-medium.neutral700--text Non-taxable subtotal
      v-spacer
      div.text-sm.font-medium.neutral700--text {{ nonTaxableSubtotal | displayPrice($currency) }}

    //- Current tax
    div.d-flex.align-center.mt-2
      div.text-xs.font-medium.neutral700--text Tax ({{ taxRate }}%)
      v-spacer
      div.text-sm.font-medium.neutral700--text {{ taxAmount | displayPrice($currency) }}

    v-divider.my-3

    //- Desired total input
    div.text-xs.font-medium.mt-2 Desired total amount
    v-text-field.text-sm.round-xl.mt-2(v-model="desiredTotal" placeholder="0" outlined dense hide-details :prefix="$currency.symbol" background-color="neutral100" type="number")

    //- Adjustment taxable checkbox
    .d-flex.align-center.my-4
      yz-checkbox(v-model="isAdjustmentTaxable" label="Adjustment is taxable" label-gap="8px")

    //- Calculated adjustment result
    div.d-flex.align-center.mt-3
      div.text-xs.font-medium.neutral700--text Calculated adjustment
      v-spacer
      div.text-md.font-semibold(:class="adjustmentDisplayColor") {{ calculatedAdjustment === 0 ? '' : calculatedAdjustment > 0 ? '-' : '+' }} {{ Math.abs(calculatedAdjustment) | displayPrice($currency) }}

    //- Apply button
    div.mt-4
      y-button(label="Apply" size="sm" block :disabled="!desiredTotal || !calculatedAdjustment" @click="onApply")
</template>

<script>
export default {
  props: {
    show: {
      type: Boolean,
      default: false
    },
    invoice: {
      type: Object,
      required: true,
      default: () => ({})
    }
  },
  data() {
    return {
      desiredTotal: null,
      isAdjustmentTaxable: true
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.show
      },
      set(val) {
        this.$emit('update:show', val)
      }
    },
    // Extract products from invoice
    products() {
      return this.invoice?.products || []
    },
    // Extract tax rate from invoice
    taxRate() {
      return this.invoice?.extra_data?.tax_rate?.rate || 0
    },
    // Set for O(1) lookup of tax-excluded items
    taxExcludeItemsSet() {
      const excludeItems = this.invoice?.extra_data?.tax_exclude_items || []
      return new Set(excludeItems)
    },
    // Calculate taxable subtotal (items NOT in taxExcludeItems)
    taxableSubtotal() {
      return this.products
        .filter(p => !this.taxExcludeItemsSet.has(p.id))
        .reduce((sum, p) => sum + (p.price * p.quantity), 0)
    },
    // Calculate non-taxable subtotal (items IN taxExcludeItems)
    nonTaxableSubtotal() {
      return this.products
        .filter(p => this.taxExcludeItemsSet.has(p.id))
        .reduce((sum, p) => sum + (p.price * p.quantity), 0)
    },
    // Total subtotal for display
    subtotal() {
      return this.taxableSubtotal + this.nonTaxableSubtotal
    },
    // Calculated tax amount for display
    taxAmount() {
      return Math.round(this.taxableSubtotal * (this.taxRate / 100))
    },
    calculatedAdjustment() {
      if (!this.desiredTotal) return 0

      // Convert desired total to cents
      const desiredTotalInCents = Math.round(this.desiredTotal * this.$currency.multiplier)

      if (this.isAdjustmentTaxable) {
        // Taxable adjustment: affects taxable base before tax calculation
        const taxRateDecimal = this.taxRate / 100
        const taxMultiplier = 1 + taxRateDecimal

        // Calculate what the adjusted taxable subtotal needs to be
        const adjustedTaxableSubtotal = (desiredTotalInCents - this.nonTaxableSubtotal) / taxMultiplier

        // Calculate the invoice_discount needed (only affects taxable portion)
        const invoiceDiscount = this.taxableSubtotal - adjustedTaxableSubtotal

        // Round to currency precision
        return Math.round(invoiceDiscount)
      } else {
        // Non-taxable adjustment: flat adjustment after tax
        const taxRateDecimal = this.taxRate / 100
        const currentTotal = Math.round(this.taxableSubtotal * (1 + taxRateDecimal)) + this.nonTaxableSubtotal

        // Invoice discount = current total - desired total
        return Math.round(currentTotal - desiredTotalInCents)
      }
    },
    adjustmentDisplayColor() {
      if (!this.calculatedAdjustment) return 'neutral700--text'
      return this.calculatedAdjustment >= 0 ? 'danger500--text' : 'success500--text'
    }
  },
  methods: {
    onApply() {
      this.$emit('apply', {
        adjustment: this.calculatedAdjustment,
        isAdjustmentTaxable: this.isAdjustmentTaxable
      })
    },
    onClose() {
      this.$emit('update:show', false)
    }
  }
}
</script>

<style scoped></style>
